import { PropsWithChildren, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useUserContext } from '../../hooks/userContext';
import { Pages, isAdminUser } from '../../utils';
import { CircularProgress, Box } from '@mui/material';
import { usePaymentSettings } from '../../contexts/PaymentSettingsContext';

/**
 * RegistrationGuard ensures that users who haven't completed registration
 * cannot access protected routes like dashboard and paid features.
 * 
 * Users must have onboardingStatus === 'active' to access protected content.
 * Otherwise, they are redirected back to registration to complete their account.
 */
export const RegistrationGuard: React.FC<PropsWithChildren> = ({ children }) => {
  const { user, loading } = useUserContext();
  const { isPaymentEnabled, isTestMode } = usePaymentSettings();
  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    if (loading) return;

    // If no user is logged in, redirect to login
    if (!user || !user.id) {
      navigate(Pages.LOGIN);
      return;
    }

    // Admins bypass all registration checks
    if (isAdminUser(user)) {
      return;
    }

    // Check if user has completed registration
    const onboardingStatus = user.onboardingStatus || 'pending_email_verification';
    const isOnboardingHub = location.pathname === Pages.YOUR_HUB;
    const isMembershipSelection =
      location.pathname === Pages.DESIGNER_MEMBERSHIP_SELECTION;
    const isProfilePage =
      location.pathname === Pages.PROFILE ||
      location.pathname === Pages.MY_ACCOUNT ||
      location.pathname === Pages.SETTINGS;
    const isPendingProfileCompletion =
      onboardingStatus === 'pending_profile_completion' ||
      onboardingStatus === 'profile_completed';
    const isDesigner = user.accountType === 'Designer';
    
    // Users with 'active' status have completed registration
    // In beta mode, designers with 'pending_profile_completion' status should be allowed to access dashboard
    // so they can complete their application
    // NOTE: 'profile_completed' is a legacy invalid status - treat it like 'pending_profile_completion'
    const isBetaMode = !isPaymentEnabled() || isTestMode();
    const canAccessDashboard = onboardingStatus === 'active' || 
      (isBetaMode && (onboardingStatus === 'pending_profile_completion' || onboardingStatus === 'profile_completed') && user.accountType === 'Designer');
    
    if (isDesigner) {
      return;
    }

    if (!canAccessDashboard) {
      if (isProfilePage) {
        return;
      }

      if (
        (isOnboardingHub && isPendingProfileCompletion) ||
        (isMembershipSelection && isPendingProfileCompletion && isDesigner)
      ) {
        return;
      }

      if (isPendingProfileCompletion) {
        navigate(Pages.YOUR_HUB);
        return;
      }

      // Redirect to login page instead of registration to avoid infinite loop
      // The login page will handle directing them to registration if needed
      navigate(Pages.LOGIN);
      return;
    }
  }, [user, loading, navigate, location.pathname, isPaymentEnabled, isTestMode]);

  // Show loading while checking user status
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // If user hasn't completed registration, don't render children
  // Allow admin users, users with active status, and designers with pending_profile_completion in beta mode
  // NOTE: 'profile_completed' is a legacy invalid status - treat it like 'pending_profile_completion'
  const onboardingStatus = user?.onboardingStatus || 'pending_email_verification';
  const isOnboardingHub = location.pathname === Pages.YOUR_HUB;
  const isMembershipSelection =
    location.pathname === Pages.DESIGNER_MEMBERSHIP_SELECTION;
  const isProfilePage =
    location.pathname === Pages.PROFILE ||
    location.pathname === Pages.MY_ACCOUNT ||
    location.pathname === Pages.SETTINGS;
  const isPendingProfileCompletion =
    onboardingStatus === 'pending_profile_completion' ||
    onboardingStatus === 'profile_completed';
  const isDesigner = user?.accountType === 'Designer';
  const isBetaMode = !isPaymentEnabled() || isTestMode();
  const canAccess = user && user.id && (
    isAdminUser(user) || 
    isDesigner ||
    onboardingStatus === 'active' ||
    (isBetaMode && isPendingProfileCompletion && user.accountType === 'Designer') ||
    (isOnboardingHub && isPendingProfileCompletion) ||
    (isMembershipSelection && isPendingProfileCompletion && isDesigner) ||
    isProfilePage
  );
  
  if (!canAccess) {
    return null;
  }

  // User has completed registration or is admin, render protected content
  return <>{children}</>;
};
